import DataGrid from 'devextreme-react/data-grid';
import dxDataGrid, { ContextMenuPreparingEvent } from 'devextreme/ui/data_grid';
import { EventInfo, Cancelable } from 'devextreme/events';

import { TColumns } from 'types';

/* -------------------- */

export const bindResizeObserver = (instance?: dxDataGrid<unknown, unknown>) => {
    if (instance && instance.element()) {
        const { ResizeObserver } = (window as any);     // seems to be a problem with the type definition with CRL!

        const rsObsever =
            new ResizeObserver(() => {
                if (instance?.element()) {
                    forceRowRender(instance, true);
                } else {
                    rsObsever.disconnect();
                }
            });
        rsObsever.observe(instance.element());
    }
};

export const onContentReady = (columns: TColumns) => (evt: EventInfo<dxDataGrid<unknown, unknown>>) => {
    const instance = evt.component;

    if (instance && columns) {
        for (const col of columns) {
            // known issue in the grid - if there are fixed columns and virtual scrolling is enabled then (sometimes) scrollbars can disappear
            // but it works correctly if fixed columns are set after the grid has rendered - so doing this dynamically here
            instance.columnOption(col.field, 'fixed', col.fixed);
            // similar issue when grouping
            instance.columnOption(col.field, 'groupIndex', col.groupIndex);
        }

        //console.log("## onContentReady");
        forceRowRender(instance);
    }
};

export const forceRowRender = (instance: dxDataGrid<unknown, unknown>, forceRefresh?: boolean) => {
    if (instance?.totalCount() && instance.getScrollable()) {
        // sometimes, with virtual scrolling enabled, several rows at the bottom of the grid are not rendered correctly on first load or on resize
        // however, any form of scrolling fixes the issue - looks like a race condition in rendering
        // several questions about this online, but none of the suggested fixes work
        // the fix below appears to work reliably - after rendering, if there are data records but not enough are currently shown, trigger an update with a 1px scroll
        const visibleRowsCount = instance.getVisibleRows().length;
        const rowsview = instance.element().querySelector('.dx-datagrid-rowsview');

        if (visibleRowsCount && rowsview) {
            const firstRow = rowsview.querySelector('.dx-row');
            if (firstRow) {
                const expectedVisibleRowsCount = Math.round(Math.min(rowsview.clientHeight / firstRow.clientHeight, instance.totalCount()));

                if (visibleRowsCount < expectedVisibleRowsCount) {
                    (window as any).requestIdleCallback(() => {
                        const scrollable = instance.getScrollable();
                        if (scrollable) {
                            scrollable.scrollBy(1);
                        }
                    }, { timeout: 40 });
                }
                if (forceRefresh) {
                    (window as any).requestIdleCallback(() => {
                        instance.updateDimensions();

                        const scrollable = instance.getScrollable();
                        if (scrollable) {
                            scrollable.scrollBy(1);
                        }
                    }, { timeout: 40 });
                }
            }
        }
    }
};

/* -------------------- */

export const setLoadPanelForFetching = (refDataGrid: DataGrid<unknown, unknown> | null, isFetching: boolean | undefined, loadPanelText: string, loadingMessage: string | undefined) => {
    if (refDataGrid?.instance) {
        if (isFetching) {
            refDataGrid.instance.beginCustomLoading(loadPanelText);
            setTimeout(() => {
                const el = refDataGrid.instance.element().querySelector('div.dx-loadpanel-message');
                if (el) {
                    if (loadingMessage) {
                        el.innerHTML = `<div>${loadPanelText}</div><div style="font-style: italic;">${loadingMessage}</div>`;
                    } else {
                        el.innerHTML = loadPanelText;
                    }
                }
            }, 10);
        } else {
            refDataGrid.instance.endCustomLoading();
        }
    }
};

/* -------------------- */

type TContextMenuEvent = ContextMenuPreparingEvent<unknown, unknown> & {
    event?: Event;
};

export const handleContextMenu = (evt: TContextMenuEvent) => {
    if (evt.target === 'header' && evt.column?.allowSorting) {
        // context menu on header of a sortable column - allow devextreme grid to show context menu
        return;
    }

    // suppress default browser context menu
    if (evt.event) {
        evt.event.preventDefault();
    }
    return false;
};

/* -------------------- */

// suppress the "Loading Overlay" during some short-lived operations - not needed and distracting to the user

export const handleRowExpandCollapseStart = (evt: EventInfo<dxDataGrid<unknown, unknown>>) => {
    const overlay = evt.component.element().querySelector('.dx-overlay.dx-loadpanel');
    if (overlay) {
        (overlay as HTMLElement).style.display = 'none';
    }
};

export const handleRowExpandCollapseEnd = (evt: EventInfo<dxDataGrid<unknown, unknown>>) => {
    const overlay = evt.component.element().querySelector('.dx-overlay.dx-loadpanel');
    if (overlay) {
        window.requestAnimationFrame(() => { (overlay as HTMLElement).style.display = 'none'; });
    }
};

/* -------------------- */

export const handleCancelableEvent = (cancel: boolean) =>
    (evt: Cancelable) => {
        evt.cancel = cancel;
    };
